import { gql } from '@apollo/client';

/**
 * Access-request workflow — shared query shapes + row types for the /access
 * pages (list / new / detail) so the three surfaces can't drift.
 */

export const ACCESS_REQUEST_FIELDS = gql`
  fragment AccessRequestFields on AccessRequestType {
    id
    requesterEmail
    resourceType
    entityId
    resourceName
    reason
    reasonEditedAt
    reasonEditedBy
    maintainerEmail
    maintainerSignoff
    maintainerSignoffNote
    maintainerSignoffAt
    currentLayer
    status
    decidedBy
    decisionNote
    decidedAt
    decisionNoteEditedAt
    decisionNoteEditedBy
    durationKind
    expiresAt
    riskTier
    createdAt
  }
`;

export interface AccessAssignee {
  reviewerEmail: string;
  layer: number;
  assignedBy: string;
  assignedAt: string;
}

export interface AccessAnswer {
  prompt: string;
  answer: string;
  sortOrder: number;
}

export interface AccessEvent {
  id: string;
  kind: string;
  actorEmail: string;
  note?: string;
  meta: string; // JSON string
  createdAt: string;
  editedAt?: string;
}

export interface AccessRequest {
  id: string;
  requesterEmail: string;
  resourceType: string;
  entityId?: string;
  resourceName: string;
  reason?: string;
  reasonEditedAt?: string;
  reasonEditedBy?: string;
  maintainerEmail: string;
  maintainerSignoff?: string;
  maintainerSignoffNote?: string;
  maintainerSignoffAt?: string;
  currentLayer: number;
  status: string;
  decidedBy?: string;
  decisionNote?: string;
  decidedAt?: string;
  decisionNoteEditedAt?: string;
  decisionNoteEditedBy?: string;
  durationKind?: string;
  expiresAt?: string;
  riskTier?: string;
  createdAt: string;
  assignees?: AccessAssignee[];
  answers?: AccessAnswer[];
  events?: AccessEvent[];
}

export interface AccessReviewer {
  id: string;
  email: string;
  displayName?: string;
  layer: number;
  createdBy: string;
  createdAt: string;
}

export interface RequestQuestion {
  id: string;
  prompt: string;
  helpText?: string;
  resourceType?: string;
  required: boolean;
  sortOrder: number;
  active: boolean;
}

export const ACCESS_REVIEWERS = gql`
  query AccessReviewers {
    accessReviewers {
      id
      email
      displayName
      layer
      createdBy
      createdAt
    }
  }
`;

export interface MentionUser {
  email: string;
  displayName?: string | null;
}

export const MENTIONABLE_USERS = gql`
  query MentionableUsers {
    mentionableUsers {
      email
      displayName
    }
  }
`;

/** Badge tint per request status (globals.css .badge variants). */
export function statusBadgeClass(status: string): string {
  if (status === 'approved') return 'badge confirmed';
  if (status === 'denied' || status === 'cancelled') return 'badge inferred';
  return 'badge';
}

/** Group a reviewer list by layer, ascending. */
export function byLayer(reviewers: AccessReviewer[]): [number, AccessReviewer[]][] {
  const m = new Map<number, AccessReviewer[]>();
  for (const r of reviewers) {
    m.set(r.layer, [...(m.get(r.layer) ?? []), r]);
  }
  return [...m.entries()].sort((a, b) => a[0] - b[0]);
}
